import { useMemo, useState, forwardRef } from "react";
import { ChevronDown, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { COUNTRIES, type Country, DEFAULT_COUNTRY } from "@/lib/countries";
import { cn } from "@/lib/utils";

interface Props {
  value: { country: Country; local: string };
  onChange: (next: { country: Country; local: string }) => void;
  placeholder?: string;
  autoComplete?: string;
  id?: string;
  disabled?: boolean;
  className?: string;
}

export const PhoneInput = forwardRef<HTMLInputElement, Props>(
  ({ value, onChange, placeholder = "771234567", autoComplete = "tel", id, disabled, className }, ref) => {
    const [open, setOpen] = useState(false);

    return (
      <>
        <div className={cn("flex gap-2", className)}>
          <button
            type="button"
            disabled={disabled}
            onClick={() => setOpen(true)}
            className={cn(
              "flex h-11 items-center gap-1 rounded-lg border border-input bg-background px-3 text-base font-medium shrink-0",
              "hover:bg-accent transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
            aria-label={`Pays : ${value.country.name}`}
          >
            <span className="text-xl leading-none">{value.country.flag}</span>
            <span className="text-sm tabular-nums">+{value.country.dial}</span>
            <ChevronDown className="size-3 opacity-50" />
          </button>
          <Input
            ref={ref}
            id={id}
            type="tel"
            inputMode="tel"
            autoComplete={autoComplete}
            placeholder={placeholder}
            disabled={disabled}
            value={value.local}
            onChange={(e) => onChange({ ...value, local: e.target.value.replace(/[^\d\s]/g, "") })}
          />
        </div>

        <CountryPicker
          open={open}
          onOpenChange={setOpen}
          selected={value.country}
          onSelect={(c) => {
            onChange({ ...value, country: c });
            setOpen(false);
          }}
        />
      </>
    );
  }
);
PhoneInput.displayName = "PhoneInput";

interface PickerProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  selected: Country;
  onSelect: (c: Country) => void;
}

function CountryPicker({ open, onOpenChange, selected, onSelect }: PickerProps) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.dial.includes(q.replace(/^\+/, ""))
    );
  }, [query]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="p-4 pb-2">
          <DialogTitle>Choisir un pays</DialogTitle>
        </DialogHeader>

        <div className="px-4 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              autoFocus
              placeholder="Rechercher (nom, code, +221…)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-2 pb-2">
          {filtered.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Aucun pays trouvé pour « {query} »
            </p>
          ) : (
            <ul className="space-y-0.5">
              {filtered.map((c) => (
                <li key={c.code}>
                  <button
                    type="button"
                    onClick={() => onSelect(c)}
                    className={cn(
                      "w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors",
                      "hover:bg-accent",
                      c.code === selected.code && "bg-primary/10 ring-1 ring-primary/30"
                    )}
                  >
                    <span className="text-2xl leading-none">{c.flag}</span>
                    <span className="flex-1 text-sm font-medium">{c.name}</span>
                    <span className="text-sm text-muted-foreground tabular-nums">+{c.dial}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t p-2">
          <Button variant="ghost" className="w-full" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Re-export pour faciliter l'import
export { DEFAULT_COUNTRY };
